import React, { useState, useEffect } from 'react'
import { Dispatch } from 'redux'
import { connect } from 'react-redux'
import withPermissionCheck from '../../core/access-control/with-permission-check'
import { Permission } from '../../core/auth0'

import Layout from '../layouts/layout'
import Wrapper from '../layouts/wrapper'
import {
  Food,
  foodsActions,
  createEmptyFoodTranslations,
} from '../../core/foods'
import { brandsActions, Brand, DEFAULT_BRAND_CODE } from '../../core/brands'
import { LANGUAGE_CODES, SupportedLanguageCode } from '../../core/i18n'
import RootState from '../../core/root-state'
import FoodInfoForm from '../components/new-food/food-info-form'
import { strings } from '../components/new-food/new-food-content'
import styles from '../styles/modules/new-food/new-food.module.scss'
import Button from '../components/button'
import PageLanguageTabs from '../components/common/page-language-tabs'
import { BrandCode } from '../components/common'
import { roleType } from '../../core/auth0'

interface Props {
  isLoading: boolean
  role: roleType
  brands: Brand[]
  createFood: (food: Food, brandCode?: string) => void
  fetchBrands: () => void
}

const NewFoodScreen: React.FC<Props> = (props: Props) => {
  const { fetchBrands } = props
  const [pageActiveLanguage, setPageActiveLanguage] = useState<
    SupportedLanguageCode
  >(LANGUAGE_CODES.JAPANESE)
  const [selectedBrand, setSelectedBrand] = useState<BrandCode>(
    DEFAULT_BRAND_CODE
  )
  const isAdmin = props.role === roleType.ADMIN

  useEffect(() => {
    fetchBrands()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [food, setFood] = useState<Food>({
    id: null,
    name: '',
    thumbnailImageFileName: '',
    price: null,
    foodTranslations: createEmptyFoodTranslations(),
  })

  const isSavable = () => {
    const { name, price } = food
    if (name === '' || price === null || price === 0) {
      return false
    } else {
      return true
    }
  }

  const handleOnChangeFoodInfo = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const name = event.target.name
    const value = event.target.value

    if (name.startsWith('foodTranslations.')) {
      const [, language, field] = name.split('.')
      const foodTranslations = [...food.foodTranslations]
      const langIndex = foodTranslations.findIndex(t => t.langCode === language)

      if (langIndex >= 0) {
        foodTranslations[langIndex] = {
          ...foodTranslations[langIndex],
          [field]: value,
        }
      } else {
        const newTranslation: any = {
          langCode: language,
          name: '',
        }
        newTranslation[field] = value
        foodTranslations.push(newTranslation)
      }

      setFood({
        ...food,
        foodTranslations,
      })
    } else if (name === 'name' || name === 'imageURL') {
      if (name === 'imageURL') {
        setFood({ ...food, thumbnailImageFileName: value })
      } else {
        setFood({ ...food, [name]: value })
      }
    } else if (name === 'price') {
      const re = /^[0-9\b]+$/
      if (value === '') {
        setFood({ ...food, [name]: null })
      }
      if (re.test(value)) {
        setFood({ ...food, [name]: Number(value) })
      }
    }
  }

  const handleOnPageLanguageChange = (language: SupportedLanguageCode) => {
    setPageActiveLanguage(language)
  }

  const handleBrandChange = (brand: BrandCode) => {
    setSelectedBrand(brand)
  }

  const handleOnClickSave = () => {
    const foodToCreate = { ...food }
    // Admin権限の場合は選択されたブランド、そうでなければデフォルトブランドを使用
    const brandCodeToUse = isAdmin ? selectedBrand : DEFAULT_BRAND_CODE
    props.createFood(foodToCreate, brandCodeToUse)
    setFood({
      id: null,
      name: '',
      thumbnailImageFileName: '',
      price: null,
      foodTranslations: createEmptyFoodTranslations(),
    })
  }

  return (
    <Layout location={'new_food'}>
      <Wrapper title="フード追加">
        <Button
          className={styles.foodSaveButton}
          title={strings.save}
          disabled={!isSavable()}
          onClick={handleOnClickSave}
        />
        {isAdmin && (
          <div className={styles.brandSelection}>
            <h3 className={styles.brandLabel}>ブランド選択</h3>
            <select
              className={styles.brandSelect}
              value={selectedBrand || ''}
              onChange={event =>
                handleBrandChange(event.target.value as BrandCode)
              }
            >
              {props.brands.map(brand => (
                <option key={brand.code} value={brand.code}>
                  {brand.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <PageLanguageTabs
          activeLanguage={pageActiveLanguage}
          onLanguageChange={handleOnPageLanguageChange}
        />
        <h3 className={styles.title}>{strings.foodInfo}</h3>
        <FoodInfoForm
          food={food}
          activeLanguage={pageActiveLanguage}
          onChange={handleOnChangeFoodInfo}
        />
      </Wrapper>
    </Layout>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    isLoading: state.foods.isLoading,
    role: state.auth0.role,
    brands: state.brands.brands,
  }
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  createFood: (food: Food, brandCode?: string) =>
    dispatch(foodsActions.createFood(food, brandCode)),
  fetchBrands: () => dispatch(brandsActions.fetchBrands()),
})

const ConnectedComponent = connect(
  mapStateToProps,
  mapDispatchToProps
)(NewFoodScreen)

export default withPermissionCheck(ConnectedComponent, {
  requiredPermission: Permission.FOOD_CREATE,
  redirectTo: '/404',
})
